import { capitalize } from 'lodash';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableExpandedRows,
    type DataTableValueArray,
} from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    View,
    YStack,
} from 'smoothie';

import {
    centsToFormattedDollars,
    formatToSimpleDateTimeString,
} from '../../../../common/utils/formatting';
import type { Member, MembershipDetails } from '../../../entityTypes';

interface DirectDepositSwitchTabPanelProps {
    membershipDetails: MembershipDetails;
}

interface SwitchDetails {
    linkTokenId: string | null;
    allocationType: string | null;
    allocationValue: string | null;
    accountName: string | null;
    lastFourAccountNumber: string | null;
    createdAt: Date | null;
    member?: Member;
    platformName?: string | null;
    outcome: string | null;
    errorCode: string | null;
    errorType: string | null;
}

const CreatedAtRenderer = ({ details }: { details: SwitchDetails }) => {
    const { createdAt, outcome } = details;
    const createdDate =
        createdAt != null
            ? formatToSimpleDateTimeString(createdAt.toString())
            : 'Unknown';

    return (
        <BodyMediumRegular
            backgroundColor={outcome !== 'success' ? '$red400' : ''}
            borderWidth={1}
            borderRadius={4}
            borderColor={outcome !== 'success' ? '$red400' : 'transparent'}
            padding={4}
        >
            {createdDate}
        </BodyMediumRegular>
    );
};

const formatValue = (value: number | null, type: string | null) => {
    if (value == null || type == null) {
        return 'Unknown';
    }

    if (type === 'percentage') {
        return `${value.toString(10)}%`;
    }
    if (type === 'amount') {
        return centsToFormattedDollars(value);
    }
    return value.toString(10);
};

const extraDataTemplate = (details: SwitchDetails) => {
    return (
        <View paddingHorizontal="4rem">
            <BodyMediumRegular marginBottom={24}>
                Troubleshooting info - Pinwheel support will often ask for Link
                Token ID
            </BodyMediumRegular>
            <DataTable value={[details]}>
                <Column field="linkTokenId" header="Link Token ID" />
                <Column field="outcome" header="Outcome" />
                <Column field="errorCode" header="Error Code" />
                <Column field="errorType" header="Error Type" />
            </DataTable>
        </View>
    );
};

export const DirectDepositSwitchTabPanel = ({
    membershipDetails,
}: DirectDepositSwitchTabPanelProps) => {
    const [expandedRows, setExpandedRows] = useState<
        DataTableExpandedRows | DataTableValueArray | undefined
    >();

    const switches = membershipDetails.directDepositSwitches ?? [];
    const data: SwitchDetails[] = switches.map((dds) => {
        const value =
            dds.action === 'full_switch' || dds.allocationType === 'remainder'
                ? '-'
                : formatValue(dds.allocationValue, dds.allocationType);
        return {
            linkTokenId: dds.linkTokenId,
            member: membershipDetails.users.find((u) => u.id === dds.userId),
            allocationType:
                dds.action === 'full_switch'
                    ? 'Full switch'
                    : capitalize(dds.allocationType ?? 'Other'),
            allocationValue: value,
            accountName: dds.accountName,
            lastFourAccountNumber: dds.lastFourAccountNumber,
            createdAt: dds.createdAt,
            platformName: dds.platformName,
            outcome: dds.outcome,
            errorCode: dds.errorCode,
            errorType: dds.errorType,
        };
    });

    // TODO maybe split out current vs history
    return (
        <YStack flex={1}>
            <BodyMediumEmphasized
                padding={8}
                marginBottom={24}
                backgroundColor="$color.orange300"
                lineHeight="1.5rem"
            >
                {`Direct deposit switches through Pinwheel only.\nSwitches made through other methods are not listed here.`}
            </BodyMediumEmphasized>
            <DataTable
                value={data}
                emptyMessage="No Pinwheel direct deposit switches"
                tableStyle={{ width: '100%' }}
                showHeaders={data.length > 0}
                sortField="createdAt"
                sortOrder={-1}
                rowExpansionTemplate={extraDataTemplate}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                    setExpandedRows(e.data);
                }}
            >
                <Column expander style={{ width: '4rem' }} />
                <Column
                    header="Member"
                    body={({ member }: SwitchDetails) => {
                        if (member != null) {
                            return `${member.firstName} ${member.lastName}`;
                        }
                        return 'Unknown';
                    }}
                />
                <Column field="platformName" header="Payroll Provider" />
                <Column field="allocationType" header="Type" />
                <Column field="allocationValue" header="Value" />
                <Column
                    header="Account"
                    body={({
                        accountName,
                        lastFourAccountNumber,
                    }: SwitchDetails) => {
                        return `${accountName} (*${lastFourAccountNumber})`;
                    }}
                />
                <Column
                    field="createdAt"
                    header="Date"
                    body={(details: SwitchDetails) => {
                        return <CreatedAtRenderer details={details} />;
                    }}
                />
            </DataTable>
        </YStack>
    );
};
